"use client";

import { useState, useMemo, useEffect } from "react";
import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { useSearchParams, useRouter } from "next/navigation";
import {
  Loader2,
  Wallet,
  ArrowUpRight,
  Phone,
  Gift,
  RefreshCw,
  AlertCircle,
  TrendingDown,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { toast } from "sonner";
import {
  useDialCreditBalance,
  useDialCreditConfig,
  useDialCreditTransactions,
  useCreateDialCreditCheckout,
} from "../hooks/useDialCredits";
import type {
  DialCreditTransaction,
  DialCreditTxnType,
} from "../services/dialCreditsService";

const TXN_LABEL: Record<DialCreditTxnType, string> = {
  purchase: "Top-up",
  usage: "AI Call",
  refund: "Refund",
  grant: "Granted",
  promo: "Promo Credit",
  adjustment: "Adjustment",
};

function formatCents(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function TxnIcon({ type }: { type: DialCreditTxnType }) {
  const base = "h-4 w-4";
  if (type === "purchase")
    return <ArrowUpRight className={`${base} text-emerald-600`} />;
  if (type === "usage") return <Phone className={`${base} text-muted-foreground`} />;
  if (type === "refund") return <RefreshCw className={`${base} text-blue-600`} />;
  if (type === "grant" || type === "promo")
    return <Gift className={`${base} text-primary`} />;
  return <TrendingDown className={`${base} text-amber-600`} />;
}

export default function DialCreditsPanel() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const balance = useDialCreditBalance();
  const config = useDialCreditConfig();
  const txns = useDialCreditTransactions({ limit: 20 });
  const checkout = useCreateDialCreditCheckout();

  const [customAmount, setCustomAmount] = useState<string>("");
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);

  useEffect(() => {
    const topup = searchParams.get("topup");
    if (topup === "success") {
      toast.success("Dial minutes added", {
        description: "Your balance will update within a few seconds.",
      });
      const params = new URLSearchParams(searchParams.toString());
      params.delete("topup");
      params.delete("session_id");
      router.replace(`/settings?${params.toString()}`, { scroll: false });
      setTimeout(() => balance.refetch(), 1500);
    } else if (topup === "cancelled") {
      toast.info("Top-up cancelled — no charge made.");
      const params = new URLSearchParams(searchParams.toString());
      params.delete("topup");
      router.replace(`/settings?${params.toString()}`, { scroll: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Direct 130's agents spend this wallet on dialing, at its own rate (tenths of a cent); AI Calling is off there.
  const dialing = balance.data?.direct130Dialing ?? null;
  // Fallback is for the math only — the displayed rate comes from the server (see TopUpCard). Direct 130's minutes
  // count a 1-minute call (its per-call fee on that minute), so the estimate never promises more than a balance buys.
  const costPerMin = dialing ? (dialing.millsPerMinute + (dialing.callFeeMills ?? 0)) / 10 : config.data?.costPerMinuteCents ?? 30;
  const displayRateCents = dialing ? dialing.millsPerMinute / 10 : config.data?.costPerMinuteCents;
  const minCents = config.data?.minPurchaseCents ?? 500;
  const maxCents = config.data?.maxPurchaseCents ?? 100000;

  const amountToCharge = useMemo(() => {
    if (selectedAmount) return selectedAmount;
    const parsed = parseFloat(customAmount);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.round(parsed * 100);
  }, [selectedAmount, customAmount]);

  const estimatedMins = useMemo(() => {
    if (!amountToCharge || !costPerMin) return 0;
    return Math.floor(amountToCharge / costPerMin);
  }, [amountToCharge, costPerMin]);

  const isAmountValid =
    amountToCharge !== null &&
    amountToCharge >= minCents &&
    amountToCharge <= maxCents;

  const handleCheckout = async () => {
    if (!amountToCharge || !isAmountValid) return;
    try {
      const result = await checkout.mutateAsync(amountToCharge);
      if (result.url) {
        // ⚠️ Consumable digital credits — the single least defensible IAP
        // category. Must never open Stripe inside the native shell.
        if (!allowCheckout("payment")) return;
        window.location.href = result.url;
      }
    } catch (err: any) {
      toast.error("Couldn't start checkout", {
        description: err?.message || "Try again in a moment.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <BalanceHero
        loading={balance.isLoading}
        balanceCents={balance.data?.balanceCents ?? 0}
        estimatedMinutes={
          dialing
            ? Math.max(0, Math.floor((balance.data?.balanceCents ?? 0) / costPerMin))
            : balance.data?.estimatedMinutesRemaining ?? 0
        }
        isLowBalance={balance.data?.isLowBalance ?? false}
        totalUsedCents={balance.data?.totalUsedCents ?? 0}
        totalMinutesUsed={balance.data?.totalMinutesUsed ?? 0}
      />

      {/* Native app: balance + history are account info (fine), but the
          top-up storefront (amount chips, prices, checkout) is web-only. */}
      {isNativePlatform() ? (
        <div className="rounded-xl border p-5 text-center text-sm text-muted-foreground">
          Adding dial minutes is available on the web version of InsuraCentral.
          To top up, log in on your computer at app.insuracentral.com.
        </div>
      ) : (
      <TopUpCard
        suggested={config.data?.suggestedAmounts ?? [1000, 2500, 5000, 10000, 25000]}
        costPerMin={costPerMin}
        displayRateCents={displayRateCents}
        inboundRateCents={dialing ? dialing.inboundMillsPerMinute / 10 : undefined}
        callFeeCents={dialing ? (dialing.callFeeMills ?? 0) / 10 : undefined}
        minCents={minCents}
        maxCents={maxCents}
        selectedAmount={selectedAmount}
        setSelectedAmount={setSelectedAmount}
        customAmount={customAmount}
        setCustomAmount={setCustomAmount}
        amountToCharge={amountToCharge}
        estimatedMins={estimatedMins}
        isAmountValid={isAmountValid}
        onCheckout={handleCheckout}
        checkoutPending={checkout.isPending}
        dialing={!!dialing}
      />
      )}

      <TransactionHistory
        loading={txns.isLoading}
        items={txns.data?.items ?? []}
        total={txns.data?.total ?? 0}
        dialing={!!dialing}
      />
    </div>
  );
}

// ─── Balance Hero ──────────────────────────────────────────────────

function BalanceHero({
  loading,
  balanceCents,
  estimatedMinutes,
  isLowBalance,
  totalUsedCents,
  totalMinutesUsed,
}: {
  loading: boolean;
  balanceCents: number;
  estimatedMinutes: number;
  isLowBalance: boolean;
  totalUsedCents: number;
  totalMinutesUsed: number;
}) {
  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-10 w-40 mt-3" />
        <Skeleton className="h-3 w-32 mt-2" />
      </div>
    );
  }

  const isEmpty = balanceCents <= 0;
  const statusLabel = isEmpty
    ? { text: balanceCents < 0 ? "Owed" : "Empty", className: "border-red-200 bg-red-50 text-red-700" }
    : isLowBalance
      ? { text: "Low balance", className: "border-amber-200 bg-amber-50 text-amber-700" }
      : { text: "Active", className: "border-emerald-200 bg-emerald-50 text-emerald-700" };

  return (
    <div className="relative overflow-hidden rounded-lg border bg-card">
      {/* Left accent stripe — primary color, brand-driven */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />

      <div className="p-6 pl-7">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium text-muted-foreground">
              <Wallet className="h-3 w-3" />
              Balance
            </div>
            <div className="mt-2 text-4xl font-semibold tracking-tight text-foreground tabular-nums">
              {balanceCents < 0 ? "−" : ""}${(Math.abs(balanceCents) / 100).toFixed(2)}
            </div>
            <div className="mt-1.5 text-sm text-muted-foreground tabular-nums">
              {estimatedMinutes.toLocaleString()} minutes remaining
            </div>
          </div>

          <span
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium ${statusLabel.className}`}
          >
            {(isEmpty || isLowBalance) && <AlertCircle className="h-3 w-3" />}
            {statusLabel.text}
          </span>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-1 border-t pt-5">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">
              Lifetime spent
            </div>
            <div className="mt-1 text-sm font-medium text-foreground tabular-nums">
              ${(totalUsedCents / 100).toFixed(2)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">
              Lifetime minutes
            </div>
            <div className="mt-1 text-sm font-medium text-foreground tabular-nums">
              {Number(totalMinutesUsed).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Top-up Card ───────────────────────────────────────────────────

function TopUpCard({
  suggested,
  costPerMin,
  displayRateCents,
  inboundRateCents,
  callFeeCents,
  minCents,
  maxCents,
  selectedAmount,
  setSelectedAmount,
  customAmount,
  setCustomAmount,
  amountToCharge,
  estimatedMins,
  isAmountValid,
  onCheckout,
  checkoutPending,
  dialing,
}: {
  /** Direct 130: the balance pays for dialing, not AI Calling. */
  dialing: boolean;
  suggested: number[];
  costPerMin: number;
  /** Server-provided rate for display; undefined until /dial-credits/config loads */
  displayRateCents?: number;
  /** Direct 130 calls in, cents per minute. */
  inboundRateCents?: number;
  /** Direct 130, per answered call out, on top of its minutes. */
  callFeeCents?: number;
  minCents: number;
  maxCents: number;
  selectedAmount: number | null;
  setSelectedAmount: (v: number | null) => void;
  customAmount: string;
  setCustomAmount: (v: string) => void;
  amountToCharge: number | null;
  estimatedMins: number;
  isAmountValid: boolean;
  onCheckout: () => void;
  checkoutPending: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-6 py-4">
        <h3 className="text-sm font-semibold text-foreground">Add dial minutes</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Pay-as-you-go. Minutes never expire.
          {typeof displayRateCents === "number" &&
            (dialing ? (
              <>
                {" "}
                Calls are charged at cost: ${(displayRateCents / 100).toFixed(4)} a minute
                {callFeeCents ? ` plus $${(callFeeCents / 100).toFixed(4)} per answered call` : ""} for Manual, Power
                or Multi-line dialing and ${((inboundRateCents ?? 0) / 100).toFixed(4)} a minute for calls in, counted
                by the started minute. A call nobody answers costs a fraction of a cent. Each call settles to its exact
                cost within about 20 minutes. Calling is free once you have $30,000 issued through Quote &amp; Enroll
                in the last 30 days. A call that runs past your balance can take it below $0; more than $10 owed
                pauses the CRM until it&apos;s paid.
              </>
            ) : (
              <>
                {" "}
                ${(displayRateCents / 100).toFixed(2)} per minute of AI conversation. Voicemails and
                unanswered calls are free.
              </>
            ))}
        </p>
      </div>

      <div className="p-6 space-y-5">
        {/* Quick amounts */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {suggested.map((cents) => {
            const dollars = cents / 100;
            const mins = Math.floor(cents / costPerMin);
            const isSelected = selectedAmount === cents;
            return (
              <button
                key={cents}
                type="button"
                onClick={() => {
                  setSelectedAmount(cents);
                  setCustomAmount("");
                }}
                className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
                  isSelected
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-background hover:border-foreground/20 hover:bg-muted/40"
                }`}
              >
                <div className="text-base font-semibold tabular-nums text-foreground">
                  ${dollars.toFixed(0)}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">
                  {mins.toLocaleString()} min
                </div>
              </button>
            );
          })}
        </div>

        {/* Custom amount */}
        <div className="space-y-1.5">
          <Label
            htmlFor="custom-amount"
            className="text-xs font-medium text-muted-foreground"
          >
            Custom amount
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              $
            </span>
            <Input
              id="custom-amount"
              type="number"
              inputMode="decimal"
              min={minCents / 100}
              max={maxCents / 100}
              step="1"
              placeholder="50"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setSelectedAmount(null);
              }}
              className="pl-7 h-10 tabular-nums"
            />
            {customAmount && estimatedMins > 0 && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground tabular-nums">
                {estimatedMins.toLocaleString()} min
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground tabular-nums">
            Min ${(minCents / 100).toFixed(0)} · Max $
            {(maxCents / 100).toLocaleString()}
          </p>
        </div>

        {/* Summary + CTA */}
        <div className="rounded-md border bg-muted/30 p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              You'll receive
            </span>
            <span className="text-xl font-semibold tabular-nums text-foreground">
              {amountToCharge ? `${estimatedMins.toLocaleString()} min` : "—"}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>{dialing ? "Dialing" : "AI outbound calling"}</span>
            <span className="tabular-nums">
              {amountToCharge ? formatCents(amountToCharge) : "$0.00"}
            </span>
          </div>

          <Button
            onClick={onCheckout}
            disabled={!isAmountValid || checkoutPending}
            className="w-full h-10 mt-4"
          >
            {checkoutPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Redirecting…
              </>
            ) : (
              <>
                Continue to checkout
                {amountToCharge ? ` · ${formatCents(amountToCharge)}` : ""}
              </>
            )}
          </Button>

          <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" />
            <span>Secured by Stripe · No subscription · Minutes never expire</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Transaction History ───────────────────────────────────────────

function TransactionHistory({
  loading,
  items,
  dialing,
}: {
  loading: boolean;
  items: DialCreditTransaction[];
  total: number;
  dialing: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-6 py-4">
        <h3 className="text-sm font-semibold text-foreground">Recent activity</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Top-ups and per-call charges
        </p>
      </div>

      {loading ? (
        <div className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <div className="mx-auto h-9 w-9 rounded-full border bg-muted/40 flex items-center justify-center">
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="mt-3 text-sm font-medium text-foreground">No activity yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {dialing ? "Top up to start dialing" : "Top up to start your first AI calling campaign"}
          </p>
        </div>
      ) : (
        <ul className="divide-y">
          {items.map((txn) => (
            <li
              key={txn.id}
              className="flex items-center gap-3 px-6 py-3 hover:bg-muted/30 transition-colors"
            >
              <div className="flex-shrink-0 h-8 w-8 rounded-full border bg-muted/40 flex items-center justify-center">
                <TxnIcon type={txn.type} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {dialing && txn.type === "usage" ? "Dialing" : TXN_LABEL[txn.type]}
                  </span>
                  {txn.type === "usage" && txn.durationSeconds && (
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      · {Math.ceil(txn.durationSeconds / 60)} min
                    </span>
                  )}
                </div>
                {txn.description && (
                  <p className="text-xs text-muted-foreground truncate">
                    {txn.description}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0 text-right">
                <div
                  className={`text-sm font-semibold tabular-nums ${
                    txn.amountCents > 0
                      ? "text-emerald-600"
                      : "text-foreground"
                  }`}
                >
                  {txn.amountCents > 0 ? "+" : ""}
                  {formatCents(txn.amountCents)}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {timeAgo(txn.createdAt)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
